import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Trash, FilePdf, EnvelopeSimple,
  ArrowsClockwise, CurrencyDollar, SealCheck, Sparkle, Storefront, Truck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, money, formatApiErrorDetail } from "../../lib/api";
import { SectionHeader, StatusChip, Money, useFocusHighlight, ConfirmDialog } from "../../components/shared";
import { CsvTools } from "../../components/CsvTools";
import { DataTable } from "../../components/DataTable";
import { SALES_EXTRA } from "../../lib/extraColumns";
import { CurrencyBadge } from "../../components/DocumentCardParts";
import { Button } from "../../components/ui/button";
import { usePerms } from "../../lib/perms";
import { MARGIN_DOC_TYPES, docMargin, MarginBadge } from "../../lib/margin";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const DOC_LABEL = {
  Quote: "Sales Quote", Order: "Sales Order", Invoice: "Sales Invoice",
  CreditMemo: "CR/Adj Note", ReturnOrder: "Return Order",
};

export function SalesDocList({ docType, variant, statusFilter: sfProp, setStatusFilter: setSfProp, colSort, setColSort, colFilters, setColFilters }) {
  const qc = useQueryClient();
  const isActive = variant === "active";
  const { can } = usePerms();
  const canCreate = isActive && docType !== "ReturnOrder" && can("sales.edit");
  const canPost = can("sales.post");
  const label = DOC_LABEL[docType];
  const backTab = { Quote: "quotes", Order: "orders", Invoice: "invoices", CreditMemo: "creditmemos", ReturnOrder: "returns" }[docType] || "orders";

  const [payOpen, setPayOpen] = useState(false);
  const [payDoc, setPayDoc] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Bank Transfer");
  const [sfLocal, setSfLocal] = useState("all");
  const statusFilter = sfProp ?? sfLocal;
  const setStatusFilter = setSfProp ?? setSfLocal;
  const [selected, setSelected] = useState([]);
  const [remOpen, setRemOpen] = useState(false);
  const [remDoc, setRemDoc] = useState(null);
  const [remText, setRemText] = useState("");
  const [remLoading, setRemLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'order'|'invoice', id, doc_no }
  const showExt = true;
  const { rowProps } = useFocusHighlight();
  const controls = setColSort ? { sort: colSort, setSort: setColSort, filters: colFilters, setFilters: setColFilters } : undefined;
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyDoc, setBuyDoc] = useState(null);
  const [buyVendorId, setBuyVendorId] = useState("");
  const [buyLines, setBuyLines] = useState([]);
  const [buyDropShip, setBuyDropShip] = useState(false);
  const [delDoc, setDelDoc] = useState(null);
  const navigate = useNavigate();
  const { data: vendorsList = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const openBuy = async (o) => {
    setBuyDoc(o);
    setBuyLines((o.lines || []).map((_, i) => i));
    setBuyDropShip(false);
    setBuyVendorId("");
    setBuyOpen(true);
    try { const { data } = await api.get(`/sales-orders/${o.id}/suggested-vendor`); if (data.vendor_id) setBuyVendorId(data.vendor_id); } catch { /* no suggestion */ }
  };
  const toggleBuyLine = (i) => setBuyLines((s) => s.includes(i) ? s.filter((x) => x !== i) : [...s, i]);

  const { data: allOrders = [] } = useQuery({ queryKey: ["sales-orders"], queryFn: async () => (await api.get("/sales-orders")).data });
  const { data: items = [] } = useQuery({ queryKey: ["items"], queryFn: async () => (await api.get("/items")).data, enabled: MARGIN_DOC_TYPES.includes(docType) });
  const { data: companyProfile } = useQuery({ queryKey: ["company-profile"], queryFn: async () => (await api.get("/company-profile")).data });
  const marginThreshold = Number(companyProfile?.low_margin_pct ?? 20);

  const docs = allOrders.filter((o) => {
    if (o.doc_type !== docType) return false;
    if (variant === "active") return !o.archived && !o.posted;
    if (variant === "posted") return o.posted;
    if (variant === "archive") return o.archived;
    return false;
  });

  const today7 = new Date(Date.now() + 0).toISOString().slice(0, 10);
  const filteredDocs = docs.filter((o) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "Overdue") return ["Open", "Partial"].includes(o.status) && o.due_date && o.due_date < today7;
    return o.status === statusFilter;
  });
  const selectableIds = docType === "Invoice" && isActive ? filteredDocs.filter((o) => !o.posted).map((o) => o.id) : [];
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const displayDocs = filteredDocs;

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["sales-orders"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["customers"] });
    qc.invalidateQueries({ queryKey: ["alerts"] });
    qc.invalidateQueries({ queryKey: ["posted-shipments"] });
  };

  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => api.put(`/sales-orders/${id}/status?status=${status}`),
    onSuccess: () => { invalidateAll(); toast.success("Status updated"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/sales-orders/${id}`),
    onSuccess: () => { invalidateAll(); toast.success("Deleted"); },
  });
  const buyForSO = useMutation({
    mutationFn: async () => (await api.post(`/sales-orders/${buyDoc.id}/create-purchase-order`, { vendor_id: buyVendorId, drop_ship: buyDropShip, line_indexes: buyLines })).data,
    onSuccess: (po) => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      qc.invalidateQueries({ queryKey: ["vendors"] });
      setBuyOpen(false);
      toast.success(`Purchase order ${po.doc_no} created for ${buyDoc.doc_no}`);
      navigate(`/purchasing?tab=orders&so=${encodeURIComponent(buyDoc.doc_no)}&focus=${po.id}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const convertToOrder = useMutation({
    mutationFn: async (id) => (await api.post(`/sales-orders/${id}/convert-to-order`)).data,
    onSuccess: (res) => { invalidateAll(); toast.success(`Quote converted to order ${res.doc_no}`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const convertToInvoice = useMutation({
    mutationFn: async (id) => (await api.post(`/sales-orders/${id}/convert-to-invoice`)).data,
    onSuccess: (res) => { invalidateAll(); toast.success(`Converted to invoice ${res.doc_no}`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const post = useMutation({
    mutationFn: async (id) => (await api.post(`/sales-orders/${id}/post`)).data,
    onSuccess: (res) => { invalidateAll(); toast.success(res.shipment ? `Posted · shipment ${res.shipment.shipment_no}` : "Document posted"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const recordPayment = useMutation({
    mutationFn: async () => (await api.post(`/sales-orders/${payDoc.id}/payments`, { amount: Number(payAmount), method: payMethod })).data,
    onSuccess: (res) => { invalidateAll(); setPayOpen(false); toast.success(res.remaining > 0 ? `Payment recorded — ${money(res.remaining)} remaining` : "Invoice fully paid"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const openPayment = (o) => { setPayDoc(o); setPayAmount(String(round2(o.total - (o.amount_paid || 0)))); setPayMethod("Bank Transfer"); setPayOpen(true); };
  const openReminder = async (o) => {
    setRemDoc(o); setRemText(""); setRemOpen(true); setRemLoading(true);
    try {
      const { data } = await api.post(`/ai/draft-quote-reminder/${o.id}`);
      setRemText(data.draft);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not draft reminder");
    } finally { setRemLoading(false); }
  };
  const sendReminder = useMutation({
    mutationFn: async () => (await api.post(`/ai/send-quote-reminder/${remDoc.id}`, { message: remText })).data,
    onSuccess: (res) => { setRemOpen(false); toast.success(`Reminder emailed to ${res.sent_to}`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const bulkPost = useMutation({
    mutationFn: async () => (await api.post("/sales-orders/bulk-post", { ids: selected })).data,
    onSuccess: (res) => {
      invalidateAll(); setSelected([]); toast.success(`Posted ${res.count} document${res.count === 1 ? "" : "s"}`);
      if (res.date_blocked?.length) { toast.error(`Not posted — posting date isn't today or falls in a closed month: ${res.date_blocked.join(", ")}.`); }
      if (res.ext_missing?.length) { toast.error(`Not posted — External Document No. is missing: ${res.ext_missing.join(", ")}.`); }
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const downloadPdf = (id) => window.open(`${API}/sales-orders/${id}/pdf`, "_blank");

  const titles = {
    active: { Quote: ["Sales Quotes", "Draft offers"], Order: ["Sales Orders", "Order entry"], Invoice: ["Sales Invoices", "Billing"], CreditMemo: ["Sales CR/Adj Notes", "Credit & adjustment notes"] },
    posted: { Invoice: ["Posted Sales Invoices", "Posted & finalized"], CreditMemo: ["Posted CR/Adj Notes", "Posted & finalized"] },
    archive: { Quote: ["Sales Quote Archives", "Converted quotes"], Order: ["Sales Order Archives", "Invoiced orders"], ReturnOrder: ["Sales Return Order Archives", "Closed returns"] },
  };
  const [title, subtitle] = (titles[variant] && titles[variant][docType]) || [label, ""];

  return (
    <>
      <SectionHeader title={title} subtitle={subtitle}>
        <div className="flex items-center gap-2 flex-wrap">
          <CsvTools
            entity={`sales-${docType.toLowerCase()}`}
            exportOnly
            label="documents"
            rows={displayDocs}
            columns={[
              { key: "doc_no", label: "doc_no" },
              { key: "doc_type", label: "type" },
              { key: "customer_name", label: "customer" },
              { key: "external_doc_no", label: "external_doc_no" },
              { key: "date", label: "date" },
              { key: "due_date", label: "due_date" },
              { key: "status", label: "status" },
              { key: "subtotal", label: "subtotal" },
              { key: "tax", label: "tax" },
              { key: "total", label: "total" },
              { key: "amount_paid", label: "amount_paid" },
            ]}
          />
          {canCreate && (
            <Button onClick={() => navigate(`/sales/doc/new?type=${docType}&tab=${backTab}`)} data-testid={`new-${docType.toLowerCase()}-button`} className="rounded-sm bg-primary hover:bg-primary/90 h-9">
              <Plus size={16} weight="bold" className="mr-1" /> {`New ${label}`}
            </Button>
          )}
        </div>
      </SectionHeader>

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-1.5" data-testid="status-filters">
          {["all", "Open", "Partial", "Paid", "Overdue"].map((s) => (
            <button
              key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                statusFilter === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40"
              }`}
            >
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>
        {docType === "Invoice" && isActive && canPost && selected.length > 0 && (
          <Button onClick={() => bulkPost.mutate()} disabled={bulkPost.isPending} data-testid="bulk-post-button" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 h-8 text-xs">
            <SealCheck size={14} weight="bold" className="mr-1" /> Post Selected ({selected.length})
          </Button>
        )}
      </div>

      <DataTable
        testid="sales-docs-table"
        data={displayDocs}
        controls={controls}
        rowKey={(o) => o.id}
        rowTestid="sales-doc-row"
        viewKey={`sales-${docType}${variant ? `-${variant}` : ""}`} extraColumns={SALES_EXTRA}
        rowProps={(o) => rowProps(o.id)}
        empty="Nothing here yet."
        leading={(docType === "Invoice" && isActive && canPost) ? {
          header: () => <input type="checkbox" data-testid="select-all-invoices" checked={selectableIds.length > 0 && selected.length === selectableIds.length} onChange={(e) => setSelected(e.target.checked ? selectableIds : [])} className="accent-primary" />,
          cell: (o) => (!o.posted ? <input type="checkbox" data-testid="select-invoice" checked={selected.includes(o.id)} onChange={() => toggleSel(o.id)} className="accent-primary" /> : null),
        } : undefined}
        columns={[
          { key: "doc_no", header: "Doc No.", filter: "text", cellClassName: "font-mono text-xs font-medium", cell: (o) => (
            <>
              <button onClick={() => navigate(`/sales/doc/${o.id}?tab=${backTab}`)} data-testid="open-sales-doc" className="text-primary hover:underline font-medium">{o.doc_no}</button>
              {o.source_order_no && <span className="text-muted-foreground ml-1">← {o.source_order_no}</span>}
            </>
          ) },
          { key: "customer_name", header: "Customer", filter: "text", cellClassName: "text-sm font-medium" },
          ...(showExt ? [{ key: "external_doc_no", header: "External Doc No.", filter: "text", cellClassName: "text-sm text-muted-foreground", cell: (o) => <span data-testid="ext-doc-no-cell">{o.external_doc_no || "—"}</span> }] : []),
          { key: "date", header: "Date", filter: "text", cellClassName: "text-sm text-muted-foreground font-mono", cell: (o) => (<>{o.date}{o.valid_until && <div className="text-xs">valid → {o.valid_until}</div>}</>) },
          { key: "status", header: "Status", filter: false, cell: (o) => (
            <div className="flex items-center gap-1.5"><StatusChip status={o.status} />{o.posted && <SealCheck size={15} className="text-emerald-600" weight="fill" title="Posted" />}</div>
          ) },
          { key: "total", header: "Total", align: "right", numeric: true, cell: (o) => (
            <div className="flex items-center justify-end gap-1.5">
              <CurrencyBadge currency={o.currency} />
              <div><Money value={o.total} className="text-sm font-medium" />{o.amount_paid > 0 && o.status !== "Paid" && (<div className="text-xs text-amber-600 tabular">{money(o.total - o.amount_paid)} due</div>)}</div>
            </div>
          ) },
          ...(MARGIN_DOC_TYPES.includes(docType) ? [{ key: "margin", header: "Margin", align: "right", numeric: true,
            accessor: (o) => { const m = docMargin(o.lines, items, o.fx_rate_to_aud || 1); return m ? Math.round(m.pct * 10) / 10 : ""; },
            cell: (o) => { const m = docMargin(o.lines, items, o.fx_rate_to_aud || 1); return <MarginBadge pct={m ? m.pct : null} threshold={marginThreshold} testid="list-margin" />; } }] : []),
          { key: "actions", header: "Actions", align: "right", sortable: false, cell: (o) => (
            <div className="flex justify-end gap-1">
              {isActive && docType === "Quote" && (<>{can("sales.edit") && <button onClick={() => setConfirm({ kind: "order", id: o.id, doc_no: o.doc_no })} data-testid="convert-to-order" title="Convert to order" className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-150"><ArrowsClockwise size={16} weight="bold" /></button>}{canPost && can("ai.use") && <button onClick={() => openReminder(o)} data-testid="quote-followup" title="Draft follow-up with Atlas" className="p-1.5 text-muted-foreground hover:text-violet-600 transition-colors duration-150"><Sparkle size={16} weight="fill" /></button>}</>)}
              {isActive && docType === "Order" && (<>{canPost && <button onClick={() => navigate(`/sales/doc/${o.id}?tab=${backTab}`)} data-testid="post-order" title="Ship / Invoice (post)" className="p-1.5 text-muted-foreground hover:text-emerald-600 transition-colors duration-150"><Truck size={16} weight="bold" /></button>}{can("purchasing.post") && <button onClick={() => openBuy(o)} data-testid="buy-for-so" title="Buy for this sales order" className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-150"><Storefront size={16} weight="bold" /></button>}</>)}
              {(docType === "Invoice" || docType === "CreditMemo") && (<button onClick={() => downloadPdf(o.id)} data-testid="invoice-pdf" title="Download PDF" className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-150"><FilePdf size={16} weight="bold" /></button>)}
              {docType === "Invoice" && canPost && (<button onClick={() => navigate(`/sales/doc/${o.id}?tab=${backTab}&email=1`)} data-testid="invoice-email" title="Email to customer" className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-150"><EnvelopeSimple size={16} weight="bold" /></button>)}
              {isActive && canPost && docType === "Invoice" && o.status !== "Paid" && (<button onClick={() => openPayment(o)} data-testid="record-payment" title="Record payment" className="p-1.5 text-muted-foreground hover:text-emerald-600 transition-colors duration-150"><CurrencyDollar size={16} weight="bold" /></button>)}
              {isActive && canPost && (docType === "Invoice" || docType === "CreditMemo") && (<button onClick={() => post.mutate(o.id)} data-testid="post-document" title="Post document" className="p-1.5 text-muted-foreground hover:text-emerald-600 transition-colors duration-150"><SealCheck size={16} weight="bold" /></button>)}
              {isActive && can("sales.delete") && (<button onClick={() => setDelDoc(o)} data-testid="delete-sales-doc" className="p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-150"><Trash size={16} /></button>)}
              {!isActive && <span className="text-xs text-muted-foreground pr-1">—</span>}
            </div>
          ) },
        ]}
      />

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CurrencyDollar size={20} className="text-primary" /> Record Payment</DialogTitle></DialogHeader>
          {payDoc && (
            <div className="space-y-4 py-2">
              <div className="border border-border bg-secondary/50 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span className="font-mono font-medium">{payDoc.doc_no}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="tabular">{money(payDoc.total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Already paid</span><span className="tabular">{money(payDoc.amount_paid || 0)}</span></div>
                <div className="flex justify-between font-bold"><span>Remaining</span><span className="tabular text-primary">{money(payDoc.total - (payDoc.amount_paid || 0))}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="overline-label">Amount</Label><Input type="number" step="0.01" data-testid="payment-amount-input" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="mt-1 rounded-sm num" /></div>
                <div>
                  <Label className="overline-label">Method</Label>
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Card">Card</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => recordPayment.mutate()} disabled={!payAmount || Number(payAmount) <= 0 || recordPayment.isPending} data-testid="save-payment-button" className="rounded-sm bg-primary hover:bg-primary/90">{recordPayment.isPending ? "Saving…" : "Record Payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={remOpen} onOpenChange={setRemOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkle size={20} className="text-primary" weight="fill" /> Atlas Follow-up Draft {remDoc && <span className="font-mono text-sm text-muted-foreground">{remDoc.doc_no}</span>}</DialogTitle></DialogHeader>
          <div className="py-2">
            {remLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Drafting a friendly reminder…
              </div>
            ) : (
              <>
                <Label className="overline-label">Email body (editable)</Label>
                <textarea data-testid="reminder-text" value={remText} onChange={(e) => setRemText(e.target.value)} rows={9} className="mt-1 w-full rounded-sm border border-border p-3 text-sm outline-none focus:border-primary resize-none" />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => sendReminder.mutate()} disabled={remLoading || !remText.trim() || sendReminder.isPending} data-testid="send-reminder-button" className="rounded-sm bg-primary hover:bg-primary/90">{sendReminder.isPending ? "Sending…" : "Send Email"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => { if (!v) setConfirm(null); }}
        title={confirm?.kind === "order" ? "Convert quote to order?" : "Convert order to invoice?"}
        description={confirm ? `${confirm.doc_no} will be converted and archived. This cannot be undone.` : ""}
        confirmLabel={confirm?.kind === "order" ? "Convert to Order" : "Convert to Invoice"}
        testid="convert-confirm-dialog"
        onConfirm={() => {
          if (confirm?.kind === "order") convertToOrder.mutate(confirm.id);
          else if (confirm?.kind === "invoice") convertToInvoice.mutate(confirm.id);
          setConfirm(null);
        }}
      />

      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="buy-for-so-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Storefront size={20} className="text-primary" /> Buy for {buyDoc?.doc_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="overline-label">Vendor</Label>
              <Select value={buyVendorId} onValueChange={setBuyVendorId}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="buy-vendor-select"><SelectValue placeholder="Select vendor to buy from" /></SelectTrigger>
                <SelectContent>
                  {vendorsList.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {buyVendorId && <p className="text-xs text-muted-foreground mt-1">Suggested from the last vendor used for these items.</p>}
            </div>
            <div>
              <Label className="overline-label">Lines to buy</Label>
              <div className="mt-1 border border-border divide-y divide-border max-h-52 overflow-y-auto">
                {(buyDoc?.lines || []).map((l, i) => (
                  <label key={i} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-secondary" data-testid="buy-line-row">
                    <input type="checkbox" data-testid={`buy-line-${i}`} checked={buyLines.includes(i)} onChange={() => toggleBuyLine(i)} className="accent-primary" />
                    <span className="flex-1 truncate">{l.description}</span>
                    <span className="text-muted-foreground tabular">{l.quantity} × {money(l.unit_price)}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="buy-drop-ship" checked={buyDropShip} onChange={(e) => setBuyDropShip(e.target.checked)} className="accent-primary" />
              <span>Drop-ship directly to <strong>{buyDoc?.customer_name}</strong></span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuyOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => buyForSO.mutate()} disabled={!buyVendorId || buyLines.length === 0 || buyForSO.isPending} data-testid="buy-for-so-confirm" className="rounded-sm bg-primary hover:bg-primary/90">{buyForSO.isPending ? "Creating…" : "Create Purchase Order"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delDoc}
        onOpenChange={(v) => { if (!v) setDelDoc(null); }}
        title="Delete this document?"
        description={delDoc ? `${delDoc.doc_no} will be permanently deleted. This cannot be undone.` : ""}
        confirmLabel="Delete"
        testid="delete-confirm-dialog"
        onConfirm={() => { if (delDoc) del.mutate(delDoc.id); setDelDoc(null); }}
      />
    </>
  );
}
